import { useEffect } from 'react'
import { useCurrentPublicLocale, publicPath } from '../lib/publicPaths'
import type { Locale } from '../lib/i18n'

type SeoPage = 'landing' | 'pricing' | 'faq' | 'regulamin' | 'polityka' | 'zwroty' | 'zgloszenie'

const SITE = 'https://edimi.ai'

const PATHS: Record<SeoPage, string> = {
  landing: '',
  pricing: '/pricing',
  faq: '/faq',
  regulamin: '/regulamin',
  polityka: '/polityka',
  zwroty: '/zwroty',
  zgloszenie: '/zgloszenie',
}

const COPY: Record<Locale, Record<SeoPage, { title: string; description: string }>> = {
  // Polish titles and descriptions were written WITHOUT Polish diacritics
  // ("edity wideo dla muzykow", "Sprawdz", "platnosci"). That string is what a
  // visitor sees in the browser tab and in Google — it reads as a machine
  // translation of our own product name.
  // VB-3 (25.09): tytuł strony głównej = jej H1 (sesja 15 zostawiła tu starsze zdanie),
  // waluta to token (reguła 34; teksty prawne mają własne, wersjonowane słowo), bez
  // stawek (należą do API i do właściciela), trzy platformy i jeden separator „—”.
  // 26.09 (Domi): „fanbase” nie jest słowem zakazanym — zdanie sesji 15 wolno tu
  // przywrócić, gdy właściciel tak zechce; dziś tytuł równa się H1.
  pl: {
    landing: {
      title: 'EDIMI — Cud nie ma terminu. Twoje publikacje mają.',
      description: 'Jeden utwór, cała seria editów, każdy pocięty inaczej. EDIMI publikuje je na Twoich kontach TikTok, Instagram i YouTube przez oficjalne API, w rytmie, który zatwierdzasz.',
    },
    pricing: {
      title: 'Cennik — EDIMI',
      description: 'Plany EDIMI: dostęp do oprogramowania i miesięczna pula tokenów na edity, publikacje i pocięcia. Nieudana operacja nic nie kosztuje. Start za darmo, bez karty.',
    },
    faq: {
      title: 'Najczęstsze pytania — EDIMI',
      description: 'Pytania o EDIMI, generator fanowskich editów: seria zamiast jednego editu, Twoje własne konta TikTok, Instagram i YouTube, oficjalne API, tokeny i zwroty.',
    },
    regulamin: {
      title: 'Regulamin — EDIMI',
      description: 'Regulamin korzystania z serwisu EDIMI: konto, płatności, treści użytkownika i zasady publikacji na TikToku, Instagramie i YouTube.',
    },
    polityka: {
      title: 'Polityka prywatności — EDIMI',
      description: 'Polityka prywatności EDIMI: administrator danych, cele przetwarzania, odbiorcy danych, podmioty przetwarzające i prawa użytkownika.',
    },
    zwroty: {
      title: 'Zwroty i anulowanie — EDIMI',
      description: 'Jak anulować subskrypcję EDIMI, kiedy przysługuje zwrot w ciągu 14 dni i co się dzieje, gdy czynność się nie uda.',
    },
    zgloszenie: {
      title: 'Zgłoś nielegalną treść — EDIMI',
      description: 'Formularz zgłoszenia treści nielegalnej (art. 16 aktu o usługach cyfrowych) oraz punkt kontaktowy EDIMI. Zgłoszenie nie wymaga konta.',
    },
  },
  en: {
    landing: {
      title: 'EDIMI — A miracle has no date. Your posts do.',
      description: 'One track, a whole series of edits, each cut differently. EDIMI publishes them to your own TikTok, Instagram and YouTube accounts through the official APIs, on a schedule you approve.',
    },
    pricing: {
      title: 'Pricing — EDIMI',
      description: 'EDIMI plans: access to the software and a monthly pool of tokens for edits, publishing and cutting. A failed operation costs nothing. Start free, no card.',
    },
    faq: {
      title: 'Frequently asked questions — EDIMI',
      description: 'Questions about EDIMI, the fan edit generator: a series instead of one edit, your own TikTok, Instagram and YouTube accounts, the official APIs, tokens and refunds.',
    },
    regulamin: {
      title: 'Terms of Service — EDIMI',
      description: 'Terms for using EDIMI: accounts, payments, user content and the rules for publishing to TikTok, Instagram and YouTube.',
    },
    polityka: {
      title: 'Privacy Policy — EDIMI',
      description: 'EDIMI privacy policy covering the data controller, processing purposes, data recipients, and user rights.',
    },
    zwroty: {
      title: 'Refunds & cancellation — EDIMI',
      description: 'How to cancel an EDIMI subscription, when a 14-day refund applies, and what happens when an action fails.',
    },
    zgloszenie: {
      title: 'Report illegal content — EDIMI',
      description: 'Notice-and-action form under article 16 of the Digital Services Act, and the EDIMI contact point. No account needed to report.',
    },
  },
}

function ensureMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null
  if (!element) {
    element = document.createElement('meta')
    document.head.appendChild(element)
  }
  for (const [name, value] of Object.entries(attributes)) {
    element.setAttribute(name, value)
  }
}

function ensureLink(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector(selector) as HTMLLinkElement | null
  if (!element) {
    element = document.createElement('link')
    document.head.appendChild(element)
  }
  for (const [name, value] of Object.entries(attributes)) {
    element.setAttribute(name, value)
  }
}

export default function Seo({ page }: { page: SeoPage }) {
  const locale = useCurrentPublicLocale()
  const path = PATHS[page]
  const canonical = `${SITE}${publicPath(locale, path)}`
  const alternatePl = `${SITE}${publicPath('pl', path)}`
  const alternateEn = `${SITE}${publicPath('en', path)}`
  const copy = COPY[locale][page]

  useEffect(() => {
    document.title = copy.title
    ensureMeta('meta[name="description"]', { name: 'description', content: copy.description })
    ensureLink('link[rel="canonical"]', { rel: 'canonical', href: canonical })
    ensureLink('link[rel="alternate"][hreflang="pl"]', { rel: 'alternate', hreflang: 'pl', href: alternatePl })
    ensureLink('link[rel="alternate"][hreflang="en"]', { rel: 'alternate', hreflang: 'en', href: alternateEn })
    ensureLink('link[rel="alternate"][hreflang="x-default"]', { rel: 'alternate', hreflang: 'x-default', href: alternatePl })
    ensureMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' })
    ensureMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'EDIMI' })
    ensureMeta('meta[property="og:title"]', { property: 'og:title', content: copy.title })
    ensureMeta('meta[property="og:description"]', { property: 'og:description', content: copy.description })
    ensureMeta('meta[property="og:url"]', { property: 'og:url', content: canonical })
    ensureMeta('meta[property="og:locale"]', { property: 'og:locale', content: locale === 'pl' ? 'pl_PL' : 'en_US' })
    // Jedna karta na język (artboard 10 sesji 06): to samo zdanie po polsku i po angielsku.
    ensureMeta('meta[property="og:image"]', { property: 'og:image', content: `${SITE}/${locale === 'pl' ? 'og-image.png' : 'og-image-en.png'}` })
    ensureMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' })
  }, [alternateEn, alternatePl, canonical, copy.description, copy.title, locale])

  return null
}
